import { Card, CardContent, Dialog, DialogContent, List, ListItem, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { Alert } from '@mui/lab';
import * as api from "api/Core";
import IconButtonAdd from 'components/Buttons/IconButtonAdd';
import IconButtonDelete from 'components/Buttons/IconButtonDelete';
import { SpinnerIndicator } from 'components/CallServer';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import CardHeaderColor from 'components/MuiExtras/Card/CardHeaderColor';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiDialogTitleClose from 'components/MuiExtras/MuiDialogTitleClose';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import AppMessage from 'components/Translations/AppMessage';
import { RelationshipCreationMode } from 'models/ApiEnums';
import { TopicRelationshipDto } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { getTopicSecondViewAction } from 'views/Timeline/Components/FeedHelpers';
import TopicSelectionDrawer from 'views/Topic/Dialogs/TopicSelectionDrawer';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    topicId: string;
    topicName: string;
    onClose: (changed: boolean) => void;
}

interface IState {
    dialog: any;
    lastError: any;
    data: TopicRelationshipDto[];
    loading: boolean;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class TopicRelationshipsDialog extends React.Component<Props, IState> {

    private changed: boolean = false;

    constructor(props: Props) {
        super(props);
        this.state = {
            data: null,
            lastError: null,
            loading: true,
            dialog: null
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Topic.Dialogs.TopicRelationshipsDialog." + r, f);
    }

    ///
    private loadData() {
        api.getTopicRelationships(this.props.topicId)
            .then((data) => {
                this.setState({ data, loading: false });
            })
            .catch((error) => {
                this.setState({ loading: false, lastError: error });
            });
    }

    ///
    private handleAdd() {
        if (this.state.data.length >= 15) {
            this.props.snackbar.showMessage(this.res("LimitReachedTitle"), "error");
            return;
        }

        this.setState({
            dialog: (
                <TopicSelectionDrawer
                    onSelect={
                        (result) => {
                            if (result != null) {
                                if (this.state.data.filter(x => x.topicId == result.id).length > 0) {
                                    this.props.snackbar.showMessage(this.res("TopicAlreadySelected"), "error");
                                }
                                else {
                                    api.addManualTopicRelationship(this.props.topicId, result.id)
                                        .then((added) => {
                                            if (added) {
                                                var newData = this.state.data.slice(0);
                                                newData.push(added);
                                                newData = newData.sort((a, b) => a.name.localeCompare(b.name));

                                                if (newData.length == 15)
                                                    this.setState({ dialog: null, data: newData });
                                                else
                                                    this.setState({ data: newData });

                                                this.changed = true;
                                            }
                                        })
                                        .catch((error) => {
                                            this.setState({ dialog: null, lastError: error });
                                        });
                                }
                            }
                            return true;
                        }
                    }
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    }

    ///
    private handleDelete(item: TopicRelationshipDto) {
        this.setState({
            dialog: <MuiConfirmation
                message={this.res("ConfirmationDelete", { name: item.name })}
                onClose={
                    (result) => {
                        if (result) {
                            api.deleteManualTopicRelationship(this.props.topicId, item.topicId)
                                .then((ok) => {
                                    if (ok) {
                                        var newData = this.state.data.filter(x => x.topicId != item.topicId);
                                        this.setState(
                                            { data: newData, dialog: null },
                                            () => this.props.snackbar.showMessage(this.res("DeleteSuccess"), "success")
                                        );
                                        this.changed = true;
                                    }
                                    else {
                                        this.setState(
                                            { dialog: null },
                                            () => this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error")
                                        );
                                    }
                                })
                                .catch((error) => {
                                    this.setState({ dialog: null, lastError: error });
                                });

                        }
                        else this.setState({ dialog: null });
                    }
                }
            />
        });
    }

    ///
    private handleClose = () => {
        this.props.onClose(this.changed);
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { data, loading, lastError, dialog } = this.state;

        return (
            <Dialog
                open
                maxWidth="md"
                fullWidth
            >
                <MuiDialogTitleClose
                    title={this.res("BoxTitle")}
                    subtitle={this.props.topicName}
                    onClose={this.handleClose}
                />

                <DialogContent>

                    {dialog}

                    <Card>

                        {
                            !loading && data != null &&
                            <CardHeaderColor
                                dense
                                color="primary"
                                title={
                                    <IconButtonAdd
                                        color="white"
                                        title={this.res("AddRelationshipTitle")}
                                        onClick={() => this.handleAdd()}
                                    >
                                        {this.res("AddText")}
                                    </IconButtonAdd>
                                }
                            />
                        }

                        <CardContent style={{ height: 300, overflowY: "auto" }}>
                            {
                                lastError
                                    ? <RequestErrorReport error={lastError} />
                                    : loading
                                        ? <SpinnerIndicator />
                                        : data.length == 0
                                            ? <Alert severity="info">{this.res("ThrereAreNoRelationships")}</Alert>
                                            : (
                                                <List disablePadding>
                                                    {
                                                        data.map((item, index) => {
                                                            return (
                                                                <ListItem
                                                                    key={index}
                                                                    button
                                                                    dense
                                                                    disableGutters
                                                                    onClick={getTopicSecondViewAction(item.topicId)}
                                                                >
                                                                    <ListItemText
                                                                        disableTypography
                                                                        primary={
                                                                            item.creationMode == RelationshipCreationMode.Manual
                                                                                ? item.name
                                                                                : <MuiColor variant="success" title={this.res("InsertedByML")}>{item.name}</MuiColor>
                                                                        }
                                                                    />
                                                                    <ListItemSecondaryAction>
                                                                        <IconButtonDelete
                                                                            size="small"
                                                                            color="muted"
                                                                            onClick={() => this.handleDelete(item)}
                                                                        />
                                                                    </ListItemSecondaryAction>
                                                                </ListItem>
                                                            );
                                                        })
                                                    }
                                                </List>
                                            )
                            }
                        </CardContent>
                    </Card>
                </DialogContent>
            </Dialog >
        );
    };
}

export default withSnackbar(withTranslation()(TopicRelationshipsDialog));
